import React from 'react';

// ============================================================
// BỐ CỤC BÀN PHÍM KÝ HIỆU TOÁN
//
// Mỗi phím chèn chuỗi `insert` vào ô trả lời. Dấu □ là ô trống của mẫu
// (giống template Equation trong Word): sau khi chèn, ô □ đầu tiên được chọn
// sẵn để gõ đè; bấm Tab / "Ô tiếp" để sang ô kế tiếp.
//   - attach: chèn ngay sau phần đang bôi đen (số mũ, chỉ số) thay vì gõ đè.
//   - variant: 'fn' cho phím chữ (sin, log...), 'wide' cho phím rộng.
// Dạng chuỗi chèn vào khớp với bộ chấm trong utils/shortAnswerMatch.js.
// ============================================================

const Slot = () => <span className="quiz-vkb-slot" />;

const Frac = ({ top, bottom }) => (
    <span className="quiz-vkb-frac">
        <span className="quiz-vkb-frac-top">{top}</span>
        <span>{bottom}</span>
    </span>
);

const MatrixIcon = ({ rows, cols }) => (
    <span className="quiz-vkb-matrix" style={{ gridTemplateColumns: `repeat(${cols}, 7px)` }}>
        {Array.from({ length: rows * cols }, (_, index) => <span key={index} />)}
    </span>
);

const key = (insert, options = {}) => ({
    id: options.id || insert,
    label: options.label || insert,
    insert,
    title: options.title,
    attach: Boolean(options.attach),
    variant: options.variant,
});

const symbolKeys = (symbols, options = {}) => Array.from(symbols).map((symbol) => key(symbol, options));

const fnKey = (name, insert, title) => key(insert, { id: name, label: name, title, variant: 'fn' });

const SQRT_KEY = key('√(□)', { id: 'sqrt', label: <>√<Slot /></>, title: 'Căn bậc hai' });
const FRACTION_KEY = key('(□)/(□)', { id: 'frac', label: <Frac top={<Slot />} bottom={<Slot />} />, title: 'Phân số' });
const SQUARE_KEY = key('²', { id: 'square', label: <>x<sup>2</sup></>, title: 'Bình phương', attach: true });
const POWER_KEY = key('^(□)', { id: 'power', label: <>x<sup>n</sup></>, title: 'Lũy thừa', attach: true });

// Hàng phím nhanh hiện sẵn dưới ô nhập khi bàn phím đang thu gọn.
export const QUICK_KEYS = [
    SQRT_KEY,
    FRACTION_KEY,
    SQUARE_KEY,
    POWER_KEY,
    key('π', { title: 'Số pi' }),
    key('±', { title: 'Cộng trừ' }),
    key('≤', { title: 'Nhỏ hơn hoặc bằng' }),
    key('≥', { title: 'Lớn hơn hoặc bằng' }),
    key('≠', { title: 'Khác' }),
    key('°', { title: 'Độ', attach: true }),
];

export const KEYBOARD_TABS = [
    {
        id: 'number',
        label: '123',
        layout: 'numpad',
        groups: [{
            keys: [
                key('7'), key('8'), key('9'), key('/', { id: 'div', label: '÷', title: 'Chia' }), key('('), key(')'),
                key('4'), key('5'), key('6'), key('×', { title: 'Nhân' }), key(',', { title: 'Dấu phẩy thập phân' }), key('; ', { id: 'semicolon', label: ';', title: 'Ngăn cách nhiều nghiệm' }),
                key('1'), key('2'), key('3'), key('-', { id: 'minus', label: '−', title: 'Trừ / số âm' }), key('=', { title: 'Bằng' }), key('π', { title: 'Số pi' }),
                key('0'), key('.', { title: 'Dấu chấm' }), key('x', { title: 'Biến x' }), key('+', { title: 'Cộng' }), SQRT_KEY, SQUARE_KEY,
            ],
        }],
    },
    {
        id: 'fraction',
        label: 'Phân số',
        groups: [{
            keys: [
                FRACTION_KEY,
                key('1/(□)', { id: 'one-over', label: <Frac top="1" bottom={<Slot />} />, title: 'Một phần' }),
                key('-(□)/(□)', { id: 'neg-frac', label: <>−<Frac top={<Slot />} bottom={<Slot />} /></>, title: 'Phân số âm' }),
                key('(□)/2', { id: 'half', label: <Frac top={<Slot />} bottom="2" />, title: 'Chia hai' }),
                key('π/(□)', { id: 'pi-over', label: <Frac top="π" bottom={<Slot />} />, title: 'π phần' }),
                key('/', { id: 'slash', label: <><Slot />/<Slot /></>, title: 'Gạch chia trên một dòng' }),
                key('%', { title: 'Phần trăm' }),
            ],
        }],
        hint: 'Phân số dài nên dùng mẫu có ngoặc: (tử)/(mẫu).',
    },
    {
        id: 'radical',
        label: '√ Căn',
        groups: [{
            keys: [
                SQRT_KEY,
                key('∛(□)', { id: 'cbrt', label: <>∛<Slot /></>, title: 'Căn bậc ba' }),
                key('∜(□)', { id: 'root4', label: <>∜<Slot /></>, title: 'Căn bậc bốn' }),
                key('√[□](□)', { id: 'nth-root', label: <><sup>n</sup>√<Slot /></>, title: 'Căn bậc n' }),
                key('□√(□)', { id: 'coef-root', label: <><Slot />√<Slot /></>, title: 'Hệ số nhân căn' }),
                key('(√(□))/(□)', { id: 'root-frac', label: <Frac top={<>√<Slot /></>} bottom={<Slot />} />, title: 'Căn chia' }),
            ],
        }],
    },
    {
        id: 'subscript',
        label: 'Chỉ số',
        groups: [
            {
                keys: [
                    key('□_(□)', { id: 'sub', label: <><Slot /><sub><Slot /></sub></>, title: 'Chỉ số dưới' }),
                    key('_(□)', { id: 'sub-attach', label: <>x<sub>n</sub></>, title: 'Thêm chỉ số cho phần đang chọn', attach: true }),
                    key('x₁', { title: 'x một' }),
                    key('x₂', { title: 'x hai' }),
                    key('uₙ', { title: 'Số hạng thứ n' }),
                    key('uₙ₊₁', { title: 'Số hạng thứ n+1' }),
                ],
            },
            { title: 'Số nhỏ dưới dòng', keys: symbolKeys('₀₁₂₃₄₅₆₇₈₉₊₋', { attach: true }) },
        ],
    },
    {
        id: 'power',
        label: 'Mũ',
        groups: [
            {
                keys: [
                    key('(□)^(□)', { id: 'pow', label: <><Slot /><sup><Slot /></sup></>, title: 'Lũy thừa' }),
                    SQUARE_KEY,
                    key('³', { id: 'cube', label: <>x<sup>3</sup></>, title: 'Lập phương', attach: true }),
                    POWER_KEY,
                    key('⁻¹', { id: 'inverse', label: <>x<sup>−1</sup></>, title: 'Nghịch đảo', attach: true }),
                    key('e^(□)', { id: 'exp', label: <>e<sup><Slot /></sup></>, title: 'Hàm mũ e' }),
                    key('10^(□)', { id: 'ten-pow', label: <>10<sup><Slot /></sup></>, title: 'Lũy thừa của 10' }),
                ],
            },
            { title: 'Số nhỏ trên dòng', keys: symbolKeys('⁰¹²³⁴⁵⁶⁷⁸⁹⁺⁻', { attach: true }) },
        ],
    },
    {
        id: 'integral',
        label: 'Tích phân',
        groups: [{
            keys: [
                key('∫□dx', { id: 'int', label: <>∫<Slot />dx</>, title: 'Nguyên hàm', variant: 'wide' }),
                key('∫_(□)^(□) □dx', { id: 'int-def', label: <>∫<sub>a</sub><sup>b</sup><Slot />dx</>, title: 'Tích phân xác định', variant: 'wide' }),
                key('∑_(□)^(□) □', { id: 'sum', label: <>∑<Slot /></>, title: 'Tổng' }),
                key('dx', { title: 'Vi phân dx' }),
                key('dt', { title: 'Vi phân dt' }),
                key('+C', { title: 'Hằng số C' }),
                key('∞', { title: 'Vô cực' }),
            ],
        }],
    },
    {
        id: 'matrix',
        label: 'Ma trận',
        groups: [
            {
                title: 'Ma trận',
                keys: [
                    key('[□ □; □ □]', { id: 'm22', label: <MatrixIcon rows={2} cols={2} />, title: 'Ma trận 2×2' }),
                    key('[□ □ □; □ □ □]', { id: 'm23', label: <MatrixIcon rows={2} cols={3} />, title: 'Ma trận 2×3' }),
                    key('[□ □; □ □; □ □]', { id: 'm32', label: <MatrixIcon rows={3} cols={2} />, title: 'Ma trận 3×2' }),
                    key('[□ □ □; □ □ □; □ □ □]', { id: 'm33', label: <MatrixIcon rows={3} cols={3} />, title: 'Ma trận 3×3' }),
                    key('[□; □; □]', { id: 'm31', label: <MatrixIcon rows={3} cols={1} />, title: 'Ma trận cột' }),
                ],
            },
            {
                title: 'Tọa độ',
                keys: [
                    key('(□; □)', { id: 'coord2', label: '(x; y)', title: 'Tọa độ trong mặt phẳng', variant: 'fn' }),
                    key('(□; □; □)', { id: 'coord3', label: '(x; y; z)', title: 'Tọa độ trong không gian', variant: 'wide' }),
                ],
            },
        ],
        hint: 'Ma trận: các số trong một hàng cách nhau bằng dấu cách, các hàng cách nhau bằng dấu ;',
    },
    {
        id: 'function',
        label: 'Hàm',
        groups: [{
            keys: [
                fnKey('sin', 'sin(□)', 'Sin'),
                fnKey('cos', 'cos(□)', 'Cos'),
                fnKey('tan', 'tan(□)', 'Tan'),
                fnKey('cot', 'cot(□)', 'Cot'),
                fnKey('log', 'log(□)', 'Logarit thập phân'),
                fnKey('ln', 'ln(□)', 'Logarit tự nhiên'),
                key('log_(□)(□)', { id: 'log-base', label: <>log<sub><Slot /></sub></>, title: 'Logarit cơ số a', variant: 'fn' }),
                key('e^(□)', { id: 'exp', label: <>e<sup>x</sup></>, title: 'Hàm mũ e' }),
                key('|□|', { id: 'abs', label: <>|<Slot />|</>, title: 'Giá trị tuyệt đối' }),
                key('lim_(□→□) □', { id: 'lim', label: <>lim<sub>x→a</sub></>, title: 'Giới hạn', variant: 'fn' }),
                key('f(□)', { id: 'fx', label: 'f(x)', title: 'Hàm số f', variant: 'fn' }),
                key('→', { title: 'Tiến tới' }),
                key('∞', { title: 'Vô cực' }),
            ],
        }],
    },
    {
        id: 'symbol',
        label: 'Ký hiệu',
        groups: [
            { title: 'So sánh & phép toán', keys: symbolKeys('=≠≈<>≤≥≡±∓×÷·%') },
            {
                title: 'Tập hợp & logic',
                keys: [
                    ...symbolKeys('∈∉⊂⊄⊆⊃∪∩∖∅ℝℕℤℚ⇒⇔∀∃'),
                    key('{□}', { id: 'set', label: <>{'{'}<Slot />{'}'}</>, title: 'Tập hợp' }),
                ],
            },
            {
                title: 'Khoảng, đoạn',
                keys: [
                    key('[□; □]', { id: 'closed', label: '[a; b]', title: 'Đoạn', variant: 'fn' }),
                    key('(□; □)', { id: 'open', label: '(a; b)', title: 'Khoảng', variant: 'fn' }),
                    key('[□; □)', { id: 'half-open-right', label: '[a; b)', title: 'Nửa khoảng', variant: 'fn' }),
                    key('(□; □]', { id: 'half-open-left', label: '(a; b]', title: 'Nửa khoảng', variant: 'fn' }),
                ],
            },
            { title: 'Hình học', keys: [...symbolKeys('∠△⊥∥≅∽'), key('°', { title: 'Độ', attach: true }), ...symbolKeys('′″⌒')] },
        ],
    },
    {
        id: 'greek',
        label: 'αβγ',
        groups: [{ keys: symbolKeys('αβγδΔεθλμπρσφωΩ') }],
    },
    {
        id: 'chemistry',
        label: 'Lý – Hóa',
        groups: [
            { title: 'Chỉ số dưới (H₂O)', keys: symbolKeys('₀₁₂₃₄₅₆₇₈₉', { attach: true }) },
            { title: 'Điện tích ion (Fe³⁺)', keys: ['⁺', '⁻', '²⁺', '³⁺', '²⁻', '³⁻'].map((charge) => key(charge, { attach: true })) },
            { title: 'Ký hiệu', keys: symbolKeys('→⇌↑↓ΔΩμλ·°').concat(key('℃', { title: 'Độ C' })) },
        ],
    },
];
